import React, {createContext, PropsWithChildren, useContext, useMemo, useState} from "react";
import {B2Discount} from "../data/client.gen";

interface OrderDates {
	from?: string;
	until?: string;
	fromDateTime?: string;
	hours?: number;
	unitCounts?: number;
	datesValid?: boolean;
}

interface OrderBoxes {
	types?: Record<string, number>;
	pricing?: Record<string, number>;
	boxesValid?: boolean;
}

interface OrderDiscount {
	data?: B2Discount;
	discountValid?: boolean;
}

interface OrderCustomer {
	name?: string;
	email?: string;
	phone?: string;
	acceptTerms?: boolean;
	acceptPrivacyPolicy?: boolean;
	customerValid?: boolean;
}

interface OrderData {
	dates: OrderDates;
	boxes: OrderBoxes;
	discount: OrderDiscount;
	customer: OrderCustomer;
}

/**
 * How the booking flow was entered: the scanned cube (`…/cubes/{cubeId}` QR) and, for a specialized QR
 * (`…/cubes/{cubeId}/{slug}`), its slug.
 */
export interface OrderInitiation {
	cubeId?: string;
	slug?: string;
}

interface OrderContextProps {
	order: OrderData;
	price: number | undefined;
	/**
	 * The QR scan this booking was initiated from, if any. Kept outside {@link OrderData} so the full-replace
	 * `setData` (used by the simplified flow) can't clobber it.
	 */
	initiation: OrderInitiation | undefined;
	setData: (data: OrderData) => void;
	setDates: (dates: OrderDates) => void;
	setBoxes: (boxes: OrderBoxes) => void;
	setDiscount: (discount: OrderDiscount) => void;
	setCustomer: (customer: OrderCustomer) => void;
}

const OrderContext = createContext<OrderContextProps>({
	order: {} as OrderData,
	price: undefined,
	initiation: undefined,
	setData: () => {
		throw new Error("Context not properly initialized.");
	},
	setDates: () => {
		throw new Error("Context not properly initialized.");
	},
	setBoxes: () => {
		throw new Error("Context not properly initialized.");
	},
	setDiscount: () => {
		throw new Error("Context not properly initialized.");
	},
	setCustomer: () => {
		throw new Error("Context not properly initialized.");
	},
});

function OrderContextProvider({children, initiation}: PropsWithChildren<{ initiation?: OrderInitiation }>) {
	const [order, setOrder] = useState<OrderData>({
		dates: {},
		boxes: {},
		discount: {},
		customer: {},
	});

	const price = useMemo(() => {
		const {boxes: {types, pricing}} = order;
		if (types === undefined || pricing === undefined) return undefined;
		const sum = Object.entries(types)
			.filter(([, quantity]) => quantity > 0)
			.map(([type, quantity]) => (pricing[type] ?? 0) * quantity)
			.reduce((a, b) => a + b, 0);
		return Math.round(sum * 100) / 100;
	}, [order.boxes]);

	function setData(data: OrderData) {
		setOrder(data);
	}

	function setDates(dates: OrderDates) {
		setOrder(prev => ({
			...prev,
			dates,
		}));
	}

	function setBoxes(boxes: OrderBoxes) {
		setOrder(prev => ({
			...prev,
			boxes,
		}));
	}

	function setDiscount(discount: OrderDiscount) {
		setOrder(prev => ({
			...prev,
			discount,
		}));
	}

	function setCustomer(customer: OrderCustomer) {
		setOrder(prev => ({
			...prev,
			customer,
		}));
	}

	const value = useMemo(
		() => ({order, initiation, setData, setDates, setBoxes, setDiscount, setCustomer, price}),
		[
			order,
			initiation,
			setData,
			setDates,
			setBoxes,
			setDiscount,
			setCustomer,
			price,
		],
	);

	return (
		<OrderContext.Provider value={value}>
			{children}
		</OrderContext.Provider>
	);
}

const useOrder = () => useContext(OrderContext);

type OrderStep = "dates" | "boxes" | "checkout";

export { OrderContextProvider, OrderStep, useOrder };
